/**
 * MetricsCards Component
 * 
 * Displays key analytics metrics in a grid of cards.
 */

import type { AnalyticsOverview } from '@formflow/shared';
import './MetricsCards.css';

interface MetricsCardsProps {
  data: AnalyticsOverview | null;
  loading?: boolean;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value: string;
  };
  loading?: boolean;
}

function MetricCard({ title, value, subtitle, icon, trend, loading }: MetricCardProps) {
  return (
    <div className={`metric-card ${loading ? 'loading' : ''}`}>
      <div className="metric-card-header">
        <span className="metric-icon">{icon}</span>
        <span className="metric-title">{title}</span>
      </div>
      <div className="metric-card-body">
        {loading ? (
          <div className="metric-skeleton">
            <div className="skeleton-value" />
            <div className="skeleton-subtitle" />
          </div>
        ) : (
          <>
            <div className="metric-value">{value}</div>
            {subtitle && <div className="metric-subtitle">{subtitle}</div>}
            {trend && (
              <div className={`metric-trend trend-${trend.direction}`}>
                <span className="trend-arrow">
                  {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'}
                </span>
                <span className="trend-value">{trend.value}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

function formatDuration(seconds?: number): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}m ${secs}s`;
}

export function MetricsCards({ data, loading }: MetricsCardsProps) {
  // Calculate derived metrics
  const startRate = data 
    ? data.unique_visitors > 0 
      ? ((data.total_starts / data.unique_visitors) * 100).toFixed(1)
      : '0'
    : '0';

  return (
    <div className="metrics-cards-grid">
      <MetricCard
        icon="👁️"
        title="Form Views"
        value={data ? formatNumber(data.total_views) : '—'}
        subtitle={data ? `${formatNumber(data.unique_visitors)} unique visitors` : undefined}
        loading={loading}
      />
      <MetricCard
        icon="🚀"
        title="Form Starts"
        value={data ? formatNumber(data.total_starts) : '—'}
        subtitle={data ? `${startRate}% start rate` : undefined}
        loading={loading}
      />
      <MetricCard
        icon="✅"
        title="Submissions"
        value={data ? formatNumber(data.total_submissions) : '—'}
        subtitle={data ? `${data.conversion_rate.toFixed(1)}% completion rate` : undefined}
        loading={loading}
      />
      <MetricCard
        icon="⏱️"
        title="Avg. Time to Complete"
        value={data ? formatDuration(data.avg_completion_time_seconds) : '—'}
        subtitle={data?.avg_completion_time_seconds ? 'from start to submit' : undefined}
        loading={loading}
      />
    </div>
  );
}

export default MetricsCards;
